"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import log from "logbeacon";
import { useQueryClient } from "@tanstack/react-query";
import { deviceDetector, getItem, setItem, pubsub } from "utils";
import { useSnapshot } from "valtio";
import EdgenDrawer from "../Drawer";
import { TurnOnNotificationContent } from "./TurnOnNotificationContent";
import { LOCAL_STORAGE_KEYS } from "@/src/config";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import {
  useNotificationPermission,
  useAppPushSubscription,
  useGlobalAppModalLimit,
  isAppModalShowEligible,
  NOTIFICATION_PERMISSION_QUERY_KEY,
} from "@/src/hooks";
import { MessageType, useBridge } from "@/src/lib/bridge";
import { rootStore } from "@/src/store";
import { ModalStore } from "@store/modules/modal";

const GLOBAL_APP_MODAL_KEY_TURN_ON_NOTIFICATION = "turn-on-notification";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * 是否满足「7 天内未展示过」可再次展示
 */
function canShowBy7Day(): boolean {
  const last = getItem(
    LOCAL_STORAGE_KEYS.TURN_ON_NOTIFICATION_MODAL_LAST_SHOWN,
  );
  log.debug(
    "[TurnOnNotificationModalContainer] TURN_ON_NOTIFICATION_MODAL_LAST_SHOWN",
    last,
  );
  if (last == null) {
    return true;
  }
  return Date.now() - Number(last) >= SEVEN_DAYS_MS;
}

// App评价弹窗是否在5分钟内打开过
function isAppRatingModalOpenedIn5Minutes(): boolean {
  const last = getItem(LOCAL_STORAGE_KEYS.APP_RATING_MODAL_LAST_SHOWN);
  if (last == null) {
    return false;
  }
  return Date.now() - Number(last) < 5 * 60 * 1000;
}

/**
 * 记录本次展示时间
 */
function markShown(): void {
  setItem(LOCAL_STORAGE_KEYS.TURN_ON_NOTIFICATION_MODAL_LAST_SHOWN, Date.now());
}

export interface OpenTurnOnNotificationModalPayload {
  force?: boolean;
}

/**
 * 开启通知引导弹窗 - 容器层
 * 仅 APP 环境渲染；场景一：权限就绪且未授权 + 7 天可展示 → 打开；
 * 场景二/三：pubsub 触发，场景三传 force: true 不校验 7 天。
 */
export const TurnOnNotificationModalContainer: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [, setConfirmLoading] = useState(false);
  const visibleRef = useRef(visible);
  const queryClient = useQueryClient();
  const { request, on } = useBridge();
  const { data: notificationPermission, requestNotificationPermission } =
    useNotificationPermission();
  // const { ownerInfo, accessToken } = useSnapshot(rootStore.user);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { turnOnNotificationModalVisible } = useSnapshot(ModalStore);
  // 使用useQuery获取通知权限
  const { openAppPush } = useAppPushSubscription();
  const settingRef = useRef(false);
  const confirmingRef = useRef(false);

  visibleRef.current = visible;

  const openModal = useCallback(() => {
    log.debug("[TurnOnNotificationModalContainer] openModal called");
    if (
      ModalStore.appVersionUpdateModalVisible ||
      ModalStore.appRatingModalVisible ||
      ModalStore.lowCreditModalVisible ||
      ModalStore.trackerWelcomeModalVisible
    ) {
      return;
    }
    ModalStore.turnOnNotificationModalVisible = true;
    setVisible(true);
    markShown();
  }, []);

  const closeModal = useCallback((source?: string) => {
    log.trace("[TurnOnNotificationModalContainer] closeModal called", {
      source,
      wasVisible: visibleRef.current,
      stack: new Error().stack,
    });
    ModalStore.turnOnNotificationModalVisible = false;
    setVisible(false);
  }, []);

  useEffect(() => {
    if (!turnOnNotificationModalVisible && visible) {
      closeModal();
    }
  }, [turnOnNotificationModalVisible, visible, closeModal]);

  const { scheduleOpen, cancelPendingOpen } = useGlobalAppModalLimit(
    GLOBAL_APP_MODAL_KEY_TURN_ON_NOTIFICATION,
    {
      hideInAppChatModal: true,
      onRemoteClaim: () => closeModal("remote-claim"),
    },
  );
  const scheduleOpenRef = useRef(scheduleOpen);
  scheduleOpenRef.current = scheduleOpen;
  const cancelPendingOpenRef = useRef(cancelPendingOpen);
  cancelPendingOpenRef.current = cancelPendingOpen;

  const syncPermissionAndCloseIfGranted = useCallback(
    async (source: string) => {
      if (!request) {
        return false;
      }
      try {
        const res = await request(MessageType.NOTIFICATION_PERMISSION, {
          action: "check",
        });
        const data = (res?.data ?? {}) as { enabled?: boolean };
        if (data.enabled !== true) {
          return false;
        }
        await queryClient.invalidateQueries({
          queryKey: NOTIFICATION_PERMISSION_QUERY_KEY,
        });
        if (visibleRef.current) {
          closeModal(`sync-granted-${source}`);
        }
        if (settingRef.current) {
          settingRef.current = false;
        }
        await openAppPush();
        return true;
      } catch (err) {
        log.debug("[TurnOnNotificationModalContainer] sync permission failed", {
          source,
          err,
        });
        return false;
      }
    },
    [request, queryClient, closeModal, openAppPush],
  );

  // const getPushStatus = useCallback(async () => {
  //   const res = await getPushSetting();
  //   const currentDevice = getDeviceType();
  //   const agent = res?.data?.data?.find((item) => item.name === "agent_agent");
  //   const deviceStatus = agent?.device_status?.find(
  //     (item) => item.device_type?.toLowerCase() === currentDevice.toLowerCase(),
  //   );
  //   const openStatus = deviceStatus?.status === "OPEN";
  //   return openStatus;
  // }, [getPushSetting]);

  const handleNotificationPermission = useCallback(
    async (payload?: OpenTurnOnNotificationModalPayload) => {
      log.debug(
        "[TurnOnNotificationModalContainer] handleNotificationPermission",
        deviceDetector.isApp(),
        notificationPermission,
        isLoggedIn,
        isAppRatingModalOpenedIn5Minutes(),
      );
      // 非APP不显示 未登录不显示 权限未获取到不显示 通知权限已开不显示
      if (!deviceDetector.isApp()) {
        log.log("[TurnOnNotificationModalContainer] skip: not app");
        return;
      }
      if (!isAppModalShowEligible()) {
        log.log(
          "[TurnOnNotificationModalContainer] skip: not eligible webview",
        );
        return;
      }
      if (!isLoggedIn) {
        log.log("[TurnOnNotificationModalContainer] skip: not logged in");
        return;
      }
      if (notificationPermission === undefined) {
        log.log(
          "[TurnOnNotificationModalContainer] skip: permission undefined",
        );
        return;
      }
      if (notificationPermission.enabled) {
        log.log(
          "[TurnOnNotificationModalContainer] skip: permission already enabled",
        );
        return;
      }
      if (isAppRatingModalOpenedIn5Minutes()) {
        log.log(
          "[TurnOnNotificationModalContainer] skip: app rating modal opened in 5 minutes",
        );
        return;
      }
      if (settingRef.current && notificationPermission?.enabled) {
        settingRef.current = false;
        await openAppPush();
        return;
      }
      if (visibleRef.current || confirmingRef.current) {
        log.log(
          "[TurnOnNotificationModalContainer] skip: modal visible or confirming",
        );
        return;
      }
      if (!canShowBy7Day() && !payload?.force) {
        log.log("[TurnOnNotificationModalContainer] skip: 7-day check failed");
        return;
      }
      log.log(
        "[TurnOnNotificationModalContainer] all checks pass, scheduling openModal",
      );
      scheduleOpenRef.current(4000, openModal);
    },
    [notificationPermission, openModal, openAppPush, isLoggedIn],
  );

  // 权限 query 已变为已授权时兜底关弹窗（含 Bridge 回包丢失场景）
  useEffect(() => {
    if (!notificationPermission?.enabled || !visibleRef.current) {
      return;
    }
    closeModal("permission-query-enabled");
  }, [notificationPermission?.enabled, closeModal]);

  // 系统权限弹窗关闭后（visibility / app-foreground）重新 check，避免 Android Detail WebView 丢回包
  useEffect(() => {
    if (!deviceDetector.isApp()) {
      return;
    }

    const shouldSync = () =>
      visibleRef.current || confirmingRef.current || settingRef.current;

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !shouldSync()) {
        return;
      }
      void syncPermissionAndCloseIfGranted("visibilitychange");
    };

    const onForeground = () => {
      if (!shouldSync()) {
        return;
      }
      void syncPermissionAndCloseIfGranted("app-foreground");
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    const offForeground = on?.(MessageType.APP_FOREGROUND, onForeground);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      offForeground?.();
    };
  }, [on, syncPermissionAndCloseIfGranted]);

  // 场景一：权限查询就绪且未授权 + 7 天可展示 → 打开
  useEffect(() => {
    handleNotificationPermission();
  }, [handleNotificationPermission]);

  // 调试：挂载/卸载、visible 变化
  useEffect(() => {
    log.log("[TurnOnNotificationModalContainer] mounted");
    return () => {
      log.log("[TurnOnNotificationModalContainer] unmounted", {
        visibleAtUnmount: visibleRef.current,
      });
    };
  }, []);
  useEffect(() => {
    log.log("[TurnOnNotificationModalContainer] visible changed", {
      visible,
    });
  }, [visible]);

  // 场景二/三：监听 pubsub，根据 payload.force 决定是否校验 7 天
  useEffect(() => {
    if (!deviceDetector.isApp()) {
      return;
    }
    const unsubscribe = pubsub.on(
      pubSubKeyMap.OPEN_TURN_ON_NOTIFICATION_MODAL,
      (payload?: OpenTurnOnNotificationModalPayload) => {
        log.debug(
          "[TurnOnNotificationModalContainer] 收到 OPEN_TURN_ON_NOTIFICATION_MODAL",
          payload,
        );
        handleNotificationPermission(payload);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [handleNotificationPermission]);

  const handleConfirm = useCallback(async () => {
    confirmingRef.current = true;
    cancelPendingOpenRef.current();
    setConfirmLoading(true);
    try {
      const res = await requestNotificationPermission();
      const enabled = res?.enabled;
      const action = res?.action;
      log.debug(
        "[TurnOnNotificationModalContainer] handleConfirm dialogShown",
        res,
      );
      if (enabled) {
        closeModal("handleConfirm-enabled");
        await openAppPush();
        return;
      }
      if (action === "open-settings") {
        settingRef.current = true;
        closeModal("handleConfirm-open-settings");
        return;
      }
      closeModal("handleConfirm-default");
    } catch (err) {
      log.error("[TurnOnNotificationModal] request permission error", err);
      const synced = await syncPermissionAndCloseIfGranted("confirm-catch");
      if (!synced) {
        closeModal("handleConfirm-error");
      }
    } finally {
      confirmingRef.current = false;
      setConfirmLoading(false);
    }
  }, [
    openAppPush,
    closeModal,
    requestNotificationPermission,
    syncPermissionAndCloseIfGranted,
  ]);

  if (!deviceDetector.isApp()) {
    return null;
  }

  return (
    <EdgenDrawer
      isOpen={visible}
      onClose={() => closeModal("drawer-onClose")}
      placement="bottom"
      width="100%"
      isHiddenClose={true}
      contentClassName="pb-0 no-scrollbar"
      isMask={false}
      maskClosable={false}
      styles={{
        body: {
          padding: "24px 24px 0 24px",
          scrollbarWidth: "none",
        },
        wrapper: {
          height: "324px",
        },
      }}
    >
      <TurnOnNotificationContent
        onConfirm={handleConfirm}
        onMaybeLater={() => closeModal("maybeLater")}
      />
    </EdgenDrawer>
  );
};
